import { Box, Card, Stack, Typography, CircularProgress } from '@mui/material';

import { ProductType } from 'src/type/product';
import { ProductPriceLog } from 'src/type/product-price-log';

// ----------------------------------------------------------------------

type CurrentPriceCardProps = {
  products: ProductType[];
  loading: boolean;
  formatPrice: (price: number | undefined) => string;
  getLatestPriceForProduct: (productId: string) => ProductPriceLog | undefined;
};

export default function CurrentPriceCard({
  products,
  loading,
  formatPrice,
  getLatestPriceForProduct,
}: CurrentPriceCardProps) {
  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">ราคาปัจจุบัน</Typography>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {products
              .sort((a, b) => a.sequence - b.sequence)
              .map((product) => {
                const priceLog = getLatestPriceForProduct(product.id);
                const isSilverProduct = /เงิน|silver/i.test(product.name);
                const unitLabel = isSilverProduct ? 'ต่อกิโลกรัม' : 'ต่อบาท';
                return (
                  <Box
                    key={product.id}
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.neutral',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'fontWeightSemiBold', mb: 1 }}
                    >
                      {product.name}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{ fontWeight: 'fontWeightMedium', mb: 0.5 }}
                      >
                        สมาคม (Association)
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          ขาย ({unitLabel}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatPrice(
                            isSilverProduct
                              ? (priceLog?.priceSellPerGram || 0) * 1000
                              : priceLog?.priceSellPerUnit
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          รับซื้อ ({unitLabel}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatPrice(
                            isSilverProduct
                              ? (priceLog?.priceBuyPerGram || 0) * 1000
                              : priceLog?.priceBuyPerUnit
                          )}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        color="secondary"
                        sx={{ fontWeight: 'fontWeightMedium', mt: 1, mb: 0.5 }}
                      >
                        ออนไลน์ (GCAP)
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          ขาย ({unitLabel}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatPrice(
                            isSilverProduct
                              ? (priceLog?.hshPriceSellPerGram || 0) * 1000
                              : priceLog?.hshPriceSellPerUnit
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          รับซื้อ ({unitLabel}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatPrice(
                            isSilverProduct
                              ? (priceLog?.hshPriceBuyPerGram || 0) * 1000
                              : priceLog?.hshPriceBuyPerUnit
                          )}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ fontWeight: 'fontWeightMedium', mt: 1, mb: 0.5 }}
                      >
                        กำหนดเอง (Custom)
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          ขาย ({unitLabel}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatPrice(
                            isSilverProduct
                              ? (priceLog?.customPriceSellPerGram || 0) * 1000
                              : priceLog?.customPriceSellPerUnit
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">
                          รับซื้อ ({unitLabel}):
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'fontWeightMedium' }}>
                          {formatPrice(
                            isSilverProduct
                              ? (priceLog?.customPriceBuyPerGram || 0) * 1000
                              : priceLog?.customPriceBuyPerUnit
                          )}
                        </Typography>
                      </Box>
                      {priceLog?.updatedAt && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ textAlign: 'center', mt: 1 }}
                        >
                          อัปเดต:{' '}
                          {new Date(priceLog.updatedAt).toLocaleString('th-TH', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                );
              })}
          </Box>
        )}
      </Stack>
    </Card>
  );
}
